import WebApp from '@twa-dev/sdk';
import { getMockTelegramWebApp, getMockUser } from './mockTelegram';
import {
  TG_TEXT_LIGHT, TG_HINT_LIGHT, ACTION_LIGHT, ON_ACTION_LIGHT,
  TG_BG_LIGHT, tgChrome,
} from '../constants/colors';

class TelegramService {
  constructor() {
    this.isMockAuthEnabled = process.env.REACT_APP_MOCK_AUTH === 'true';
    this.isRunningInTelegram = this.checkIfRunningInTelegram();
    this.tg = this.isRunningInTelegram ? WebApp : (this.isMockAuthEnabled ? getMockTelegramWebApp() : null);
    this.user = null;
    this.isReady = false;
    this.readyPromise = null;
    this.initApp();
  }

  checkIfRunningInTelegram() {
    // Проверяем наличие Telegram WebApp и непустой initData
    return typeof window !== 'undefined' &&
           window.Telegram?.WebApp &&
           window.Telegram.WebApp.initData &&
           window.Telegram.WebApp.initData.length > 0;
  }

  initApp() {
    // Создаем Promise для отслеживания готовности
    this.readyPromise = new Promise((resolve) => {
      // Если нет Telegram и мок отключён — просто помечаем готовым
      if (!this.tg) {
        this.isReady = true;
        resolve();
        return;
      }

      try {
        // Expand app to full height first
        this.tg.expand();

        // Request fullscreen mode only on mobile platforms (not desktop)
        const isMobilePlatform = this.tg.platform === 'android' ||
                                 this.tg.platform === 'ios' ||
                                 this.tg.platform === 'android_x';

        if (isMobilePlatform && typeof this.tg.requestFullscreen === 'function') {
          try {
            this.tg.requestFullscreen();
          } catch (error) {
            // Fullscreen not supported on this platform
          }
        }

        // Enable closing confirmation
        this.tg.enableClosingConfirmation();

        // Disable vertical swipes to prevent accidental closing
        if (this.tg.platform === 'android' || this.tg.platform === 'ios') {
          this.tg.disableVerticalSwipes();
        }

        // Цвет хедера до монтирования ThemeContext — та же логика, что и в нём:
        // выбор пользователя, иначе тема телефона. Иначе на светлой теме
        // хедер мигнёт тёмным на старте.
        const savedTheme = localStorage.getItem('spbnef_theme');
        const isDark = savedTheme
          ? savedTheme === 'dark'
          : this.tg.colorScheme !== 'light';
        this.tg.setHeaderColor(tgChrome(isDark));
        this.tg.setBackgroundColor(tgChrome(isDark));

        // Request safe area insets. Заодно выставляет --tg-viewport-height
        // и держит её актуальной по viewportChanged
        this.setupSafeArea();

        // Set theme colors
        if (this.tg.themeParams) {
          document.documentElement.style.setProperty(
            '--tg-theme-bg-color',
            this.tg.themeParams.bg_color || TG_BG_LIGHT
          );
          document.documentElement.style.setProperty(
            '--tg-theme-text-color',
            this.tg.themeParams.text_color || TG_TEXT_LIGHT
          );
          document.documentElement.style.setProperty(
            '--tg-theme-hint-color',
            this.tg.themeParams.hint_color || TG_HINT_LIGHT
          );
          document.documentElement.style.setProperty(
            '--tg-theme-link-color',
            this.tg.themeParams.link_color || ACTION_LIGHT
          );
          document.documentElement.style.setProperty(
            '--tg-theme-button-color',
            this.tg.themeParams.button_color || ACTION_LIGHT
          );
          document.documentElement.style.setProperty(
            '--tg-theme-button-text-color',
            this.tg.themeParams.button_text_color || ON_ACTION_LIGHT
          );
        }

        // Get user data
        if (this.isRunningInTelegram && this.tg.initDataUnsafe?.user) {
          this.user = this.tg.initDataUnsafe.user;
        } else if (!this.isRunningInTelegram && this.isMockAuthEnabled) {
          // Mock user — только если REACT_APP_MOCK_AUTH=true
          this.user = getMockUser();
        }

        // Setup fullscreen event handlers
        if (this.tg.onEvent) {
          this.tg.onEvent('fullscreenChanged', () => {});
          this.tg.onEvent('fullscreenFailed', () => {});
        }

        // Ready
        this.tg.ready();
        this.isReady = true;

        resolve();
      } catch (error) {
        this.isReady = true; // Все равно помечаем готовым, чтобы не блокировать
        resolve();
      }
    });
  }

  setupSafeArea() {
    // Функция для обновления CSS переменных
    const updateSafeAreaVars = () => {
      const safeArea = this.tg.safeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 };
      const contentSafeArea = this.tg.contentSafeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 };

      // Устанавливаем системные safe area
      document.documentElement.style.setProperty('--safe-area-top', `${safeArea.top}px`);
      document.documentElement.style.setProperty('--safe-area-bottom', `${safeArea.bottom}px`);
      document.documentElement.style.setProperty('--safe-area-left', `${safeArea.left}px`);
      document.documentElement.style.setProperty('--safe-area-right', `${safeArea.right}px`);

      // Устанавливаем content safe area для элементов Telegram
      document.documentElement.style.setProperty('--content-safe-area-top', `${contentSafeArea.top}px`);
      document.documentElement.style.setProperty('--content-safe-area-bottom', `${contentSafeArea.bottom}px`);
      document.documentElement.style.setProperty('--content-safe-area-left', `${contentSafeArea.left}px`);
      document.documentElement.style.setProperty('--content-safe-area-right', `${contentSafeArea.right}px`);

      // Высота видимой области. В Telegram vh не равен ей: клиент отдаёт
      // реальное значение здесь. Обновляется вместе с safe area — иначе
      // после разворачивания приложения или открытия клавиатуры значение
      // остаётся тем, что было на старте, и панель уезжает за кромку.
      //
      // Только внутри Telegram. В браузере SDK отдаёт заглушку — число,
      // снятое один раз и не следящее за окном. Подложка всплывающих
      // панелей бралась этой высоты, оказывалась короче окна, и панель
      // висела посреди экрана, а под ней было видно незатемнённую
      // страницу. Вне Telegram остаётся `100vh` из index.css — он верен
      if (this.isRunningInTelegram && this.tg.viewportHeight) {
        document.documentElement.style.setProperty(
          '--tg-viewport-height',
          `${this.tg.viewportHeight}px`
        );
      }
    };

    // Подписка на события изменения safe area (попробуем разные варианты событий)
    if (this.tg.onEvent) {
      this.tg.onEvent('safeAreaChanged', updateSafeAreaVars);
      this.tg.onEvent('contentSafeAreaChanged', updateSafeAreaVars);
      this.tg.onEvent('viewportChanged', updateSafeAreaVars);
    }

    // Обновление при изменении viewport
    if (typeof this.tg.onViewportChanged === 'function') {
      this.tg.onViewportChanged(updateSafeAreaVars);
    }

    // Первоначальная установка значений
    updateSafeAreaVars();

    // Повторная установка через небольшую задержку (на случай если значения приходят асинхронно)
    setTimeout(updateSafeAreaVars, 100);
    setTimeout(updateSafeAreaVars, 500);
  }

  // Метод ожидания готовности
  async waitForReady() {
    if (this.isReady) {
      return Promise.resolve();
    }
    return this.readyPromise;
  }

  isRunningInTelegramApp() {
    return this.isRunningInTelegram;
  }

  getTelegramUser() {
    return this.user;
  }

  getUser() {
    return this.user;
  }

  getTelegramWebApp() {
    return this.tg;
  }

  getUserName() {
    const user = this.getUser();
    if (!user) return null;
    return user.first_name || user.username || 'User';
  }

  getUserPhoto() {
    const user = this.getUser();
    if (!user) return null;
    return user.photo_url || null;
  }

  showMainButton(text, onClick) {
    this.tg.MainButton.setText(text);
    this.tg.MainButton.show();
    this.tg.MainButton.onClick(onClick);
  }

  hideMainButton() {
    this.tg.MainButton.hide();
  }

  showBackButton(onClick) {
    this.tg.BackButton.show();
    this.tg.BackButton.onClick(onClick);
  }

  hideBackButton() {
    this.tg.BackButton.hide();
  }

  hapticFeedback(type = 'light') {
    try {
      if (this.tg.HapticFeedback) {
        this.tg.HapticFeedback.impactOccurred(type);
      }
    } catch (error) {
      // Handle haptic feedback error silently
    }
  }

  setThemeColors(isDark) {
    try {
      const bg = tgChrome(isDark);
      this.tg.setHeaderColor(bg);
      this.tg.setBackgroundColor(bg);
    } catch (error) {
      // ignore
    }
  }

  close() {
    this.tg.close();
  }

  openLink(url) {
    this.tg.openLink(url);
  }

  openTelegramLink(url) {
    this.tg.openTelegramLink(url);
  }

  shareUrl(url, text) {
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    this.openTelegramLink(shareUrl);
  }

  addToHomeScreen() {
    try {
      // Сначала пробуем через window.Telegram.WebApp напрямую (наиболее актуальный API)
      if (window.Telegram?.WebApp?.addToHomeScreen) {
        window.Telegram.WebApp.addToHomeScreen();
        return;
      }
      // Fallback на SDK
      if (this.tg?.addToHomeScreen) {
        this.tg.addToHomeScreen();
      }
    } catch (error) {
      // ignore
    }
  }

  // Получить start_param из URL или initDataUnsafe
  getStartParam() {
    // Сначала пробуем из initDataUnsafe
    if (this.isRunningInTelegram && this.tg.initDataUnsafe?.start_param) {
      return this.tg.initDataUnsafe.start_param;
    }

    // Затем из URL параметра
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('tgWebAppStartParam');
    }

    return null;
  }
}

const telegramService = new TelegramService();

export default telegramService;
